import React, { useState, useRef, useCallback, useEffect } from 'react';
import s from './SiteNav.module.css';
import bgImage from '../images/שובל רוימי תדמית 1.webp';

const WA_LINK = 'https://wa.me/972504499737?text=' + encodeURIComponent('היי שובל, הגעתי מהאתר ואשמח לשמוע פרטים.');
const INSTA_URL = 'https://www.instagram.com/shoval_roymi/';

const NAV_ITEMS = [
  { id: 'about', label: 'אודות הסטודיו' },
  { id: 'process', label: 'תהליך העבודה בסטודיו' },
  { id: 'faq', label: 'שאלות תשובות' },
  { id: 'projects', label: 'פרוייקטים' },
  { id: 'footer', label: 'יצירת קשר' },
];

export default function SiteNav() {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showBtn, setShowBtn] = useState(false);

  const overlayRef = useRef(null);
  const line1 = useRef(null);
  const line2 = useRef(null);
  const line3 = useRef(null);
  const linkRefs = useRef([]);
  const sepRef = useRef(null);
  const contactRef = useRef(null);
  const tlRef = useRef(null);
  // gsap is loaded on demand (keeps it out of the initial bundle).
  const gsapRef = useRef(null);
  const gsapPromiseRef = useRef(null);

  const ensureGsap = useCallback(() => {
    if (gsapRef.current) return Promise.resolve(gsapRef.current);
    if (!gsapPromiseRef.current) {
      gsapPromiseRef.current = import('gsap').then((mod) => {
        gsapRef.current = mod.gsap;
        return mod.gsap;
      });
    }
    return gsapPromiseRef.current;
  }, []);

  useEffect(() => () => { if (tlRef.current) tlRef.current.kill(); }, []);

  // Show hamburger after scrolling past hero
  useEffect(() => {
    const onScroll = () => setShowBtn(window.scrollY > window.innerHeight * 0.7);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Warm up gsap once the menu button appears, so the first open is instant.
  useEffect(() => {
    if (!showBtn) return undefined;
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(() => ensureGsap())
      : setTimeout(() => ensureGsap(), 200);
    return () => {
      if (window.cancelIdleCallback && window.requestIdleCallback) {
        window.cancelIdleCallback(idle);
      } else {
        clearTimeout(idle);
      }
    };
  }, [showBtn, ensureGsap]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      const sw = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${sw}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => { document.body.style.overflow = ''; document.body.style.paddingRight = ''; };
  }, [isOpen]);

  const openMenu = useCallback(async () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setIsOpen(true);
    const gsap = await ensureGsap();
    if (tlRef.current) tlRef.current.kill();

    const tl = gsap.timeline({ onComplete: () => setIsAnimating(false) });
    tlRef.current = tl;

    // Lines → X
    tl.to(line1.current, { rotation: 45, y: 7.5, duration: 0.4, ease: 'power2.inOut' }, 0)
      .to(line2.current, { opacity: 0, scaleX: 0, duration: 0.2, ease: 'power2.in' }, 0)
      .to(line3.current, { rotation: -45, y: -7.5, duration: 0.4, ease: 'power2.inOut' }, 0);

    // Circle reveal
    tl.to(overlayRef.current, {
      clipPath: 'circle(150% at calc(100% - 44px) 44px)',
      duration: 0.8, ease: 'power3.inOut',
    }, 0.1);

    // Stagger links
    tl.to(linkRefs.current.filter(Boolean), {
      opacity: 1, y: 0, duration: 0.5, stagger: 0.08, ease: 'power2.out',
    }, 0.45);

    // Separator + contact
    tl.to(sepRef.current, { opacity: 1, duration: 0.4, ease: 'power2.out' }, 0.75);
    tl.to(contactRef.current, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, 0.8);
  }, [isAnimating, ensureGsap]);

  const closeMenu = useCallback(async (afterClose) => {
    if (isAnimating) return;
    setIsAnimating(true);
    const gsap = await ensureGsap();
    if (tlRef.current) tlRef.current.kill();

    const tl = gsap.timeline({
      onComplete: () => {
        setIsOpen(false);
        setIsAnimating(false);
        linkRefs.current.forEach(el => el && gsap.set(el, { opacity: 0, y: 30 }));
        if (sepRef.current) gsap.set(sepRef.current, { opacity: 0 });
        if (contactRef.current) gsap.set(contactRef.current, { opacity: 0, y: 20 });
        if (afterClose) afterClose();
      },
    });
    tlRef.current = tl;

    tl.to([...linkRefs.current.filter(Boolean), sepRef.current, contactRef.current], {
      opacity: 0, duration: 0.2, ease: 'power2.in',
    }, 0);
    tl.to(overlayRef.current, {
      clipPath: 'circle(0% at calc(100% - 44px) 44px)',
      duration: 0.6, ease: 'power3.inOut',
    }, 0.15);
    tl.to(line1.current, { rotation: 0, y: 0, duration: 0.35, ease: 'power2.inOut' }, 0.2)
      .to(line2.current, { opacity: 1, scaleX: 1, duration: 0.25, ease: 'power2.out' }, 0.3)
      .to(line3.current, { rotation: 0, y: 0, duration: 0.35, ease: 'power2.inOut' }, 0.2);
  }, [isAnimating, ensureGsap]);

  const toggle = useCallback(() => {
    isOpen ? closeMenu() : openMenu();
  }, [isOpen, openMenu, closeMenu]);

  const handleNav = useCallback((id) => {
    closeMenu(() => {
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    });
  }, [closeMenu]);

  const setLinkRef = useCallback((i) => (el) => { linkRefs.current[i] = el; }, []);

  return (
    <>
      <button
        className={`${s.hamburger} ${showBtn || isOpen ? s.hamburgerShow : ''}`}
        onClick={toggle}
        aria-label={isOpen ? 'סגירת תפריט' : 'פתיחת תפריט'}
        aria-expanded={isOpen}
      >
        <span className={s.hLine} ref={line1} />
        <span className={s.hLine} ref={line2} />
        <span className={`${s.hLine} ${s.hLineShort}`} ref={line3} />
      </button>

      <div ref={overlayRef} className={`${s.overlay} ${isOpen ? s.overlayOpen : ''}`} aria-hidden={!isOpen}>
        <div className={s.overlayBg} />
        <div className={s.overlayImg}><img src={bgImage} alt="" loading="lazy" decoding="async" /></div>

        <div className={s.overlayContent}>
          <nav className={s.navLinks}>
            {NAV_ITEMS.map((item, i) => (
              <button key={item.id} ref={setLinkRef(i)} className={s.navLink}
                onClick={() => handleNav(item.id)} type="button"
              >{item.label}</button>
            ))}
          </nav>

          <div ref={sepRef} className={s.sep} />

          <div ref={contactRef} className={s.contact}>
            <a href="tel:+972504499737" className={s.contactItem}>050-449-9737</a>
            <a href={INSTA_URL} target="_blank" rel="noopener noreferrer" className={s.contactItem}>
              <svg className={s.instaIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <circle cx="12" cy="12" r="5" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
              @shoval_roymi
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
